import { buildStakeRows } from "./supernovaExitQuote";
import { METRIC } from "./supernovaSummary";

/**
 * supernovaActivity — the "Recent activity" feed, derived from service data.
 * No React, no I/O, no formatting of dates or numbers.
 *
 * WHAT IT CAN SEE, AND WHAT IT CANNOT. The only stake history this app has is
 * the set of OPEN lots supernovaChain.getStaked returns, so each open lot yields
 * exactly one event: the stake that opened it, dated by its own `stakedAtMs`.
 * That is real service data, not a fixture.
 *
 * TODO(backend, supervised): UNSTAKES AND CLOSED LOTS ARE NOT IN THIS FEED. They
 * only exist in the staking contract's TokenUnstaked / TokenEmergencyUnstaked
 * logs, whose parameter lists are unverified (supernovaContracts.js,
 * SUPERNOVA_ENGINE_DESIGN.md V-1) — nothing in this app decodes logs. Inventing
 * an unstake row, or presenting this partial list as a complete history, would
 * misstate a member's record, so the feed carries CLOSED_LOTS_NOTE on screen and
 * the ACTIVITY_KIND vocabulary already has the slot the unstake events will fill.
 *
 * COMPLIANCE: an event's XP figure is what that lot has accrued so far, straight
 * out of the model. It is an in-app points balance with no monetary value, and
 * no return, yield, income, price, or projection appears here.
 */

/** The mockup's cap: the six most recent events. */
export const ACTIVITY_LIMIT = 6;

export const ACTIVITY_KIND = {
  STAKE: "stake",
  /* Reserved for when the event-log read lands — see the TODO above. Nothing in
     this build ever produces one. */
  UNSTAKE: "unstake",
};

export const ACTIVITY_UNAVAILABLE_NOTE =
  "Your stake activity comes from the staking contract and isn't connected yet. " +
  "Nothing about your on-chain stakes has changed.";

export const ACTIVITY_EMPTY_NOTE =
  "No stake activity to show. Staking a Supernova locks it in the contract for " +
  "the duration you pick and starts accruing XP daily.";

export const CLOSED_LOTS_NOTE =
  "Only stakes that are still open appear here. Unstakes and lots you've " +
  "already closed live in the staking contract's event log, which this app " +
  "doesn't read yet — so this is a partial history, not your full record.";

/**
 * @typedef {Object} ActivityEvent
 * @property {string} key
 * @property {string} kind        an ACTIVITY_KIND
 * @property {string} lotId
 * @property {number} atMs        when it happened
 * @property {number} tokenId
 * @property {string} tokenName
 * @property {number} qty
 * @property {number} lockDays
 * @property {string} lockLabel   "90 days" / "3 years"
 * @property {string} lockName    "90-day" / "3-year"
 * @property {number} earnedXp    accrued by this lot so far
 * @property {boolean} matured
 */

/**
 * @param {Object} input
 * @param {Object|null} [input.staked]        getStaked() result
 * @param {string} [input.stakedStatus]       METRIC status for that read
 * @param {string|null} [input.stakedNote]    why stakes are unavailable
 * @param {string|null} [input.stakedMessage] member-safe copy for a failed read
 * @param {number|Date} [input.nowMs]         injectable clock
 * @param {number} [input.limit]
 * @returns {{
 *   status: string,
 *   events: ActivityEvent[],
 *   total: number|null,
 *   truncated: boolean,
 *   unreadable: number|null,
 *   note: string|null,
 *   message: string|null,
 *   closedLotsKnown: boolean,
 * }}
 */
export function buildActivity(input = {}) {
  const {
    staked = null,
    stakedStatus = METRIC.UNAVAILABLE,
    stakedNote = null,
    stakedMessage = null,
    nowMs = Date.now(),
    limit = ACTIVITY_LIMIT,
  } = input;

  const now = nowMs instanceof Date ? nowMs.getTime() : nowMs;

  const base = {
    status: stakedStatus,
    events: [],
    total: null,
    truncated: false,
    unreadable: null,
    note: null,
    message: null,
    /* Always false in this build: closed lots are invisible to us. */
    closedLotsKnown: false,
  };

  switch (stakedStatus) {
    case METRIC.LOADING:
      return { ...base, status: METRIC.LOADING };
    case METRIC.IDLE:
      return { ...base, status: METRIC.IDLE };
    case METRIC.ERROR:
      return {
        ...base,
        status: METRIC.ERROR,
        message: stakedMessage || "Couldn't read your stake activity from Arbitrum.",
      };
    case METRIC.READY:
      break;
    default:
      return {
        ...base,
        status: METRIC.UNAVAILABLE,
        note: stakedNote || ACTIVITY_UNAVAILABLE_NOTE,
      };
  }

  /* Same rows the Active stakes table renders, so a lot cannot be described one
     way there and another way here. A lot we couldn't rate has no trustworthy
     date or quantity, so it is counted rather than rendered as an event. */
  const rows = buildStakeRows(staked, now);
  const rated = rows.filter((r) => r.readable);

  const events = rated
    .map((row) => ({
      key: `${ACTIVITY_KIND.STAKE}-${row.lotId}`,
      kind: ACTIVITY_KIND.STAKE,
      lotId: row.lotId,
      atMs: row.stakedAtMs,
      tokenId: row.tokenId,
      tokenName: row.tokenName,
      qty: row.qty,
      lockDays: row.lockDays,
      lockLabel: row.lockLabel,
      lockName: row.lockName,
      earnedXp: row.earnedXp,
      matured: row.matured,
    }))
    /* Most recent first — the order a member scans for "what did I just do". */
    .sort((a, b) => b.atMs - a.atMs);

  return {
    ...base,
    status: METRIC.READY,
    events: events.slice(0, limit),
    total: events.length,
    truncated: events.length > limit,
    unreadable: rows.length - rated.length,
    note: CLOSED_LOTS_NOTE,
  };
}

export default buildActivity;
